import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Logo, { LOGO_SRC } from "@/components/Logo";

const FIELDS = [
  { key: "first_name", label: "First Name", type: "text", autoComplete: "given-name" },
  { key: "last_name", label: "Last Name", type: "text", autoComplete: "family-name" },
  { key: "company", label: "Company", type: "text", autoComplete: "organization" },
  { key: "position", label: "Position", type: "text", autoComplete: "organization-title" },
  { key: "age", label: "Age", type: "number", min: 14, max: 100 },
  { key: "weight_kg", label: "Weight (kg)", type: "number", step: "0.1", min: 30, max: 300 },
  { key: "email", label: "Email", type: "email", autoComplete: "email" },
  { key: "phone", label: "Phone", type: "tel", autoComplete: "tel" },
];

const EMPTY = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

export default function Signup() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        age: parseInt(form.age, 10),
        weight_kg: parseFloat(form.weight_kg),
      };
      await api.post("/signup/next-year", payload);
      setDone(true);
      toast.success("You're on the list — see you next year!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join(" • ")
        : detail || "Sign-up failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#050505] text-white px-6">
        <div className="text-center max-w-md" data-testid="signup-success">
          <CheckCircle2 className="mx-auto text-emerald-400" size={56} />
          <h1 className="font-display text-5xl mt-8 uppercase">You&apos;re in</h1>
          <p className="mt-4 text-zinc-400">
            We&apos;ve got your details. The Corporate Clash team will be in touch
            before next year&apos;s event.
          </p>
          <Link to="/" className="btn-ghost inline-flex mt-10 px-6 py-3 text-xs" data-testid="signup-home-link">
            Back to home <ArrowRight size={14} className="ml-2" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="signup-logo">
            <Logo size={28} />
          </Link>
          <span className="eyebrow text-zinc-500 hidden sm:inline">Next year sign-up</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 md:px-10 py-12">
        <div className="flex flex-col sm:flex-row gap-6 sm:items-end">
          <img
            src={LOGO_SRC}
            alt="Corporate Clash"
            className="w-28 h-28 rounded-full ring-1 ring-white/10"
          />
          <div>
            <p className="eyebrow text-rose-500">Be part of the action</p>
            <h1 className="font-display text-5xl md:text-6xl mt-3 uppercase leading-none">
              Sign up for next year
            </h1>
            <p className="mt-4 text-zinc-400 max-w-xl">
              Drop your details and we&apos;ll be in touch about training, the
              fight card, and how you can step into the ring at next year&apos;s
              Corporate Clash in Macarthur.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-12 grid sm:grid-cols-2 gap-5" data-testid="signup-form">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.key === "phone" ? "sm:col-span-2" : ""}>
              <label className="eyebrow block mb-2">{f.label}</label>
              <input
                type={f.type}
                required
                min={f.min}
                max={f.max}
                step={f.step}
                autoComplete={f.autoComplete}
                value={form[f.key]}
                onChange={(e) => update(f.key, e.target.value)}
                className="flat"
                data-testid={`signup-${f.key}-input`}
              />
            </div>
          ))}
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full py-4 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="signup-submit-btn"
            >
              {busy && <Loader2 className="animate-spin" size={18} />}
              Send My Details
            </button>
            <p className="mt-4 text-xs text-zinc-500 text-center">
              We&apos;ll only use these details to contact you about next year&apos;s
              Corporate Clash event.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
